import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import { UPDATE_PROPOSAL } from '../../graphql/proposal/mutation';
import { useNotification } from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';
import type { ProposalStatusType } from '../../types/proposal';

const STATUS_COLOR: Record<ProposalStatusType, string> = {
  in_contact: '!bg-gray-status !text-dark-blue',
  content_proposal: '!bg-dark-blue !text-white',
  contract_signing: '!bg-violet-status !text-white',
  rejected: '!bg-red-status !text-white',
  approved: '!bg-green-status !text-dark-blue',
};

// Statuses selectable from the dropdown (approved is a separate action)
const SELECTABLE_STATUSES: ProposalStatusType[] = [
  'in_contact',
  'content_proposal',
  'contract_signing',
  'rejected',
];

interface ProposalStatusDropdownProps {
  defaultStatus: ProposalStatusType;
  proposalId: string;
  refetch?: () => void;
}

const ProposalStatusDropdown = ({
  defaultStatus,
  proposalId,
  refetch,
}: ProposalStatusDropdownProps) => {
  const { t } = useTranslation('common');
  const [selectedStatus, setSelectedStatus] =
    useState<ProposalStatusType>(defaultStatus);
  const { isInfluentia } = usePermissions();
  const { notifySaveSuccess, notifySaveError } = useNotification();

  const [updateProposal] = useMutation(UPDATE_PROPOSAL);

  const statusOptions = SELECTABLE_STATUSES.map((status) => ({
    value: status,
    label: t(`proposalStatus.${status}`),
  }));

  useEffect(() => {
    setSelectedStatus(defaultStatus);
  }, [defaultStatus]);

  const handleStatusChange = (
    selectedOption: { value: string; label: string } | null,
  ) => {
    if (!selectedOption) return;
    const newStatus = selectedOption.value as ProposalStatusType;
    setSelectedStatus(newStatus);
    updateProposal({
      variables: { id: proposalId, status: newStatus },
      onCompleted: () => {
        notifySaveSuccess();
        if (refetch) refetch();
      },
      onError: () => notifySaveError(),
    });
  };

  const customStyles = {
    menuList: (base: any) => ({
      ...base,
      padding: 0,
      borderRadius: '4px',
      '::-webkit-scrollbar': { width: '6px', height: '0px' },
      '::-webkit-scrollbar-track': {
        background: 'white',
        borderRadius: '20px',
      },
      '::-webkit-scrollbar-thumb': {
        background: '#16162C',
        borderRadius: '20px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#16162C',
        borderRadius: '20px',
      },
    }),
    option: (base: any, props: any) => ({
      ...base,
      background: props.isSelected ? '#00E7FF' : 'white',
      ':hover': {
        border: 'none',
        boxShadow: 'none',
        background: '#00E7FF',
      },
    }),
    container: (base: any) => ({ ...base, width: '100%' }),
  };

  const CustomOption = (props: any) => (
    <components.Option
      className="bg-white hover:!bg-dark-blue hover:!text-white !text-dark-blue"
      {...props}
    >
      <div className="flex items-center justify-center">
        <div className="flex flex-row items-center justify-center gap-x-2 !cursor-pointer text-center rounded-10 px-7 py-1">
          <div
            className={`size-[10px] flex-none rounded-full ${STATUS_COLOR[props.data.value as ProposalStatusType]}`}
          />
          <div className="items-center text-center text-[14px] font-medium">
            {props.data.label}
          </div>
        </div>
      </div>
    </components.Option>
  );

  // If approved, show a non-interactive badge (approved is set via the approve action)
  if (selectedStatus === 'approved') {
    return (
      <div
        className={`h-[38px] text-sm px-4 rounded-md flex items-center justify-center w-full ${STATUS_COLOR.approved}`}
      >
        {t('proposalStatus.approved')}
      </div>
    );
  }

  return (
    <Select
      value={statusOptions.find((o) => o.value === selectedStatus)}
      onChange={handleStatusChange}
      options={statusOptions}
      styles={customStyles}
      components={{ Option: CustomOption }}
      classNames={{
        control: () =>
          `!border-none !shadow-none rounded-md !w-full text-center ${STATUS_COLOR[selectedStatus]}`,
        singleValue: () => `${STATUS_COLOR[selectedStatus]}`,
        indicatorSeparator: () => '!hidden',
        dropdownIndicator: () => '!hidden',
        indicatorsContainer: () => '!hidden',
      }}
      isDisabled={!isInfluentia()}
      classNamePrefix="react-select"
      isSearchable={false}
    />
  );
};

export default ProposalStatusDropdown;
